/*
 * Owner registration form (sections/owner-registration.liquid).
 *
 * Posts the form as JSON to the Shopify app proxy (data-endpoint), which
 * Shopify signs and forwards to Drupal. Drupal re-checks everything, so the
 * validation here is only for the visitor's convenience: it never decides
 * whether a registration is accepted.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-owner-reg]');
  if (!root) return;

  var form = root.querySelector('[data-owner-reg-form]');
  var thanks = root.querySelector('[data-owner-reg-thanks]');
  var summary = root.querySelector('[data-owner-reg-summary]');
  var button = root.querySelector('[data-owner-reg-submit]');
  var captchaBox = root.querySelector('[data-owner-reg-captcha]');
  var endpoint = root.getAttribute('data-endpoint') || '/apps/owner-registration';
  var siteKey = root.getAttribute('data-recaptcha-key') || '';
  var widgetId = null;
  var sending = false;

  var GENERIC = 'Something went wrong and your registration was not saved. Please try again in a few minutes.';
  var BUSY = 'We are getting a lot of registrations right now. Please try again in a few minutes.';

  // ---------------------------------------------------------------- reCAPTCHA
  // Without a site key the form cannot be submitted (the server would reject
  // it anyway), so say so instead of failing at the end.
  if (!siteKey) {
    captchaBox.textContent = 'The security check is not set up yet, so registration is unavailable.';
    button.disabled = true;
  } else {
    window.ownerRegCaptchaReady = function () {
      widgetId = window.grecaptcha.render(captchaBox, { sitekey: siteKey });
    };
    var script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit&onload=ownerRegCaptchaReady';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  // -------------------------------------------------------------------- errors
  function field(name) {
    return form.querySelector('[name="' + name + '"]');
  }

  function showError(name, message) {
    var slot = form.querySelector('[data-err-for="' + name + '"]');
    if (slot) slot.textContent = message;
    var input = field(name);
    if (input && input.type !== 'radio') input.setAttribute('aria-invalid', 'true');
  }

  function clearErrors() {
    form.querySelectorAll('[data-err-for]').forEach(function (el) { el.textContent = ''; });
    form.querySelectorAll('[aria-invalid]').forEach(function (el) { el.removeAttribute('aria-invalid'); });
    summary.hidden = true;
    summary.textContent = '';
  }

  function focusFirstError() {
    var first = form.querySelector('[aria-invalid="true"]') || form.querySelector('.owner-reg__err:not(:empty)');
    if (first && first.scrollIntoView) first.scrollIntoView({ block: 'center', behavior: 'smooth' });
    if (first && first.focus && first.tagName !== 'P') first.focus({ preventScroll: true });
  }

  // Server fields that have no input of their own map onto one that does.
  var ERROR_TARGET = { Email2: 'Email2', Region: 'Region' };

  function applyServerErrors(errors) {
    var shown = 0;
    Object.keys(errors).forEach(function (name) {
      showError(ERROR_TARGET[name] || name, errors[name]);
      shown++;
    });
    if (!shown) showSummary(GENERIC);
    focusFirstError();
  }

  function showSummary(message) {
    summary.textContent = message;
    summary.hidden = false;
    summary.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  // ----------------------------------------------------------- purchase date
  // Today in the visitor's own time zone (not the server's, not UTC), so the
  // date is right for them whatever the time of day.
  function localToday() {
    var now = new Date();
    var month = String(now.getMonth() + 1).padStart(2, '0');
    var day = String(now.getDate()).padStart(2, '0');
    return now.getFullYear() + '-' + month + '-' + day;
  }

  var purchaseDate = field('PurchasedDate');
  purchaseDate.max = localToday();
  // Most owners register right after buying; they can change it.
  if (!purchaseDate.value) purchaseDate.value = localToday();

  // ------------------------------------------------------------- client checks
  function validateLocally() {
    var ok = true;
    form.querySelectorAll('[required]').forEach(function (el) {
      if (el.type === 'radio') return;
      if (!String(el.value).trim()) {
        showError(el.name, 'This field is required.');
        ok = false;
      }
    });
    ['PurchasedCondition', 'EmailOptIn'].forEach(function (name) {
      if (!form.querySelector('[name="' + name + '"]:checked')) {
        showError(name, 'Please choose one.');
        ok = false;
      }
    });
    var email = field('Email');
    var email2 = field('Email2');
    if (email.value.trim() && email.validity.typeMismatch) {
      showError('Email', 'Enter a valid email address.');
      ok = false;
    } else if (email.value.trim().toLowerCase() !== email2.value.trim().toLowerCase() && email2.value.trim()) {
      showError('Email2', 'Emails must match.');
      ok = false;
    }
    var date = field('PurchasedDate');
    if (date.value && date.value > localToday()) {
      showError('PurchasedDate', 'The purchase date cannot be in the future.');
      ok = false;
    }
    var token = siteKey && window.grecaptcha && widgetId !== null ? window.grecaptcha.getResponse(widgetId) : '';
    if (!token) {
      showError('captcha', 'Please confirm you are not a robot.');
      ok = false;
    }
    return ok;
  }

  // -------------------------------------------------------------------- submit
  function payload() {
    var data = {};
    new FormData(form).forEach(function (value, key) { data[key] = String(value).trim(); });
    // The widget adds its own hidden copy of the token to the form; send it once.
    delete data['g-recaptcha-response'];
    data.captcha = window.grecaptcha.getResponse(widgetId);
    return data;
  }

  function resetCaptcha() {
    if (window.grecaptcha && widgetId !== null) window.grecaptcha.reset(widgetId);
  }

  function done() {
    form.hidden = true;
    thanks.hidden = false;
    thanks.scrollIntoView({ block: 'start', behavior: 'smooth' });
    thanks.focus({ preventScroll: true });
  }

  form.addEventListener('submit', function (event) {
    event.preventDefault();
    if (sending) return;
    clearErrors();
    if (!validateLocally()) {
      focusFirstError();
      return;
    }

    sending = true;
    button.disabled = true;
    var label = button.textContent;
    button.textContent = 'Sending...';

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload()),
      credentials: 'same-origin'
    })
      .then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (body) {
          return { status: response.status, body: body };
        });
      })
      .then(function (result) {
        if (result.status === 200 && result.body.ok) {
          done();
          return;
        }
        resetCaptcha();
        if (result.status === 422 && result.body.errors) {
          applyServerErrors(result.body.errors);
        } else if (result.status === 429) {
          showSummary(BUSY);
        } else {
          showSummary(GENERIC);
        }
      })
      .catch(function () {
        resetCaptcha();
        showSummary(GENERIC);
      })
      .then(function () {
        sending = false;
        button.disabled = false;
        button.textContent = label;
      });
  });

  // A field's error clears as soon as the visitor edits it.
  form.addEventListener('input', function (event) {
    var el = event.target;
    if (!el.name) return;
    var slot = form.querySelector('[data-err-for="' + el.name + '"]');
    if (slot) slot.textContent = '';
    el.removeAttribute('aria-invalid');
  });
})();
